import { resetPassword as resetPasswordService } from '../services/resetPassword'
import { create } from 'zustand'
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updatePassword as firebaseUpdatePassword,
  sendEmailVerification,
  reload,
  RecaptchaVerifier,
  PhoneAuthProvider,
  updatePhoneNumber,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth'
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '../services/firebase'
import toast from 'react-hot-toast'

export const useAuthStore = create((set, get) => ({
    // Reset password
    resetPassword: async (email) => {
      return await resetPasswordService(email)
    },
  user: null,
  userData: null,
  loading: true,
  error: null,
  recaptchaVerifier: null,
  confirmationResult: null,

  // Initialize auth listener
  initialize: () => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Get user data from Firestore
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
          if (userDoc.exists()) {
            const userData = userDoc.data()
            
            // Sync email verification status from Firebase Auth to Firestore
            if (firebaseUser.emailVerified && !userData.emailVerified) {
              await updateDoc(doc(db, 'users', firebaseUser.uid), {
                emailVerified: true,
                updatedAt: serverTimestamp(),
              })
              // Refresh user data after update
              const updatedDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
              set({
                user: firebaseUser,
                userData: { id: updatedDoc.id, ...updatedDoc.data() },
                loading: false,
              })
            } else {
              set({
                user: firebaseUser,
                userData: { id: userDoc.id, ...userData },
                loading: false,
              })
            }
          } else {
            // User document doesn't exist, but Firebase user exists
            set({
              user: firebaseUser,
              userData: null,
              loading: false,
            })
          }
        } catch (error) {
          console.error('Error fetching user data:', error)
          set({
            user: firebaseUser,
            userData: null,
            loading: false,
            error: error.message,
          })
        }
      } else {
        set({ user: null, userData: null, loading: false })
      }
    })

    return unsubscribe
  },

  // Register new user
  register: async (email, password, userData) => {
    try {
      set({ loading: true, error: null })

      // Create auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      const { uid } = userCredential.user

      // Send email verification
      try {
        await sendEmailVerification(userCredential.user, {
          url: window.location.origin + '/dashboard',
          handleCodeInApp: false,
        })
        toast.success('Te enviamos un correo de verificación')
      } catch (emailError) {
        console.warn('Error sending verification email:', emailError)
        // No bloqueamos el registro si falla el email
      }

      // Generate username based on email if not provided
      let username = userData.username
      if (!username) {
        // Extraer la parte antes del @ del email
        const emailPrefix = email.split('@')[0]
        // Limpiar caracteres especiales y convertir a minúsculas
        const cleanPrefix = emailPrefix.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
        // Agregar 4 caracteres aleatorios para unicidad
        const randomSuffix = Math.random().toString(36).substring(2, 6)
        username = `${cleanPrefix}${randomSuffix}`
      }

      // Create user document
      await setDoc(doc(db, 'users', uid), {
        email,
        username,
        fullName: userData.fullName,
        phone: userData.phone,
        phoneVerified: false, // Se verificará con OTP
        emailVerified: false, // Se verificará con link
        dateOfBirth: userData.dateOfBirth,
        authProvider: 'email',
        profileComplete: true, // Ya tiene todos los datos requeridos
        role: 'user',
        wallet: {
          deposited: 0,
          winnings: 0,
          total: 0,
        },
        kyc: {
          verified: false,
          documentSubmitted: false,
        },
        notifications: {
          email: true,
          sms: true,
          platform: true,
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })

      toast.success('Cuenta creada exitosamente')
      return { success: true, user: userCredential.user }
    } catch (error) {
      console.error('Registration error:', error)
      set({ error: error.message })
      
      let errorMessage = 'Error al crear la cuenta'
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'El email ya está registrado'
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'La contraseña debe tener al menos 6 caracteres'
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Email inválido'
      }
      
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      set({ loading: false })
    }
  },

  // Sign in
  signIn: async (email, password) => {
    try {
      set({ loading: true, error: null })
      await signInWithEmailAndPassword(auth, email, password)
      toast.success('Inicio de sesión exitoso')
      return { success: true }
    } catch (error) {
      console.error('Sign in error:', error)
      set({ error: error.message })
      
      let errorMessage = 'Error al iniciar sesión'
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMessage = 'Email o contraseña incorrectos'
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Demasiados intentos. Intenta más tarde'
      }
      
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      set({ loading: false })
    }
  },

  // Sign in with Google
  signInWithGoogle: async () => {
    try {
      set({ loading: true, error: null })
      const provider = new GoogleAuthProvider()
      provider.setCustomParameters({
        prompt: 'select_account'
      })
      
      const result = await signInWithPopup(auth, provider)
      const { uid, email, displayName, photoURL } = result.user

      // Check if user document exists
      const userDoc = await getDoc(doc(db, 'users', uid))
      
      if (!userDoc.exists()) {
        // New user - create document
        const emailPrefix = email.split('@')[0]
        const cleanPrefix = emailPrefix.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
        const randomSuffix = Math.random().toString(36).substring(2, 6)
        const username = `${cleanPrefix}${randomSuffix}`

        await setDoc(doc(db, 'users', uid), {
          email,
          username,
          fullName: displayName || email.split('@')[0],
          phone: '',
          phoneVerified: false,
          emailVerified: true, // Google ya verificó el email
          dateOfBirth: '',
          photoURL: photoURL || '',
          authProvider: 'google',
          profileComplete: false, // Necesita completar perfil
          role: 'user',
          wallet: {
            deposited: 0,
            winnings: 0,
            total: 0,
          },
          kyc: {
            verified: false,
            documentSubmitted: false,
          },
          notifications: {
            email: true,
            sms: true,
            platform: true,
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
        
        // Immediately fetch and update the store with user data
        const createdUserDoc = await getDoc(doc(db, 'users', uid))
        set({
          user: result.user,
          userData: { id: createdUserDoc.id, ...createdUserDoc.data() },
          loading: false,
        })
        
        toast.success('¡Bienvenido a Kiniela!')
        return { success: true, isNewUser: true }
      } else {
        // Existing user - update last login
        await updateDoc(doc(db, 'users', uid), {
          updatedAt: serverTimestamp(),
        })
        
        // Immediately fetch and update the store with user data
        const updatedUserDoc = await getDoc(doc(db, 'users', uid))
        set({
          user: result.user,
          userData: { id: updatedUserDoc.id, ...updatedUserDoc.data() },
          loading: false,
        })
        
        toast.success('Inicio de sesión exitoso')
        return { success: true, isNewUser: false }
      }
      
      return { success: true }
    } catch (error) {
      console.error('Google sign in error:', error)
      set({ error: error.message })
      
      let errorMessage = 'Error al iniciar sesión con Google'
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Inicio de sesión cancelado'
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup bloqueado. Permite popups para este sitio'
      }
      
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      set({ loading: false })
    }
  },

  // Sign out
  signOut: async () => {
    try {
      await firebaseSignOut(auth)
      set({ user: null, userData: null })
      toast.success('Sesión cerrada')
    } catch (error) {
      console.error('Sign out error:', error)
      toast.error('Error al cerrar sesión')
    }
  },

  // Update user data
  updateUserData: async (updates) => {
    const { user } = get()
    if (!user) return

    try {
      await updateDoc(doc(db, 'users', user.uid), {
        ...updates,
        updatedAt: serverTimestamp(),
      })

      // Refresh user data
      const userDoc = await getDoc(doc(db, 'users', user.uid))
      set({ userData: { id: userDoc.id, ...userDoc.data() } })
      
      toast.success('Perfil actualizado')
      return { success: true }
    } catch (error) {
      console.error('Update error:', error)
      toast.error('Error al actualizar perfil')
      return { success: false, error: error.message }
    }
  },

  // Update password
  updatePassword: async (newPassword) => {
    const { user } = get()
    if (!user) return

    try {
      await firebaseUpdatePassword(user, newPassword)
      toast.success('Contraseña actualizada')
      return { success: true }
    } catch (error) {
      console.error('Password update error:', error)
      
      let errorMessage = 'Error al actualizar contraseña'
      if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Necesitas volver a iniciar sesión para cambiar tu contraseña'
      }
      
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  },

  // Setup reCAPTCHA
  setupRecaptcha: (containerIdOrElement) => {
    try {
      const { recaptchaVerifier } = get()
      
      // Clear existing verifier
      if (recaptchaVerifier) {
        recaptchaVerifier.clear()
      }

      const verifier = new RecaptchaVerifier(auth, containerIdOrElement, {
        size: 'invisible',
        callback: () => {
          // reCAPTCHA solved
        },
        'expired-callback': () => {
          toast.error('reCAPTCHA expirado. Intenta de nuevo')
        }
      })

      set({ recaptchaVerifier: verifier })
      return verifier
    } catch (error) {
      console.error('reCAPTCHA setup error:', error)
      toast.error('Error al configurar verificación')
      return null
    }
  },

  // Send OTP to phone number
  sendPhoneOTP: async (phoneNumber) => {
    try {
      const { recaptchaVerifier, user } = get()
      
      if (!recaptchaVerifier) {
        throw new Error('reCAPTCHA no configurado')
      }

      if (!user) {
        throw new Error('Usuario no autenticado')
      }

      // Format phone number to E.164 format (+52...)
      const formattedPhone = phoneNumber.startsWith('+') 
        ? phoneNumber 
        : `+52${phoneNumber.replace(/\D/g, '')}`

      // Usar PhoneAuthProvider para obtener verificationId sin cambiar sesión
      const phoneProvider = new PhoneAuthProvider(auth)
      const verificationId = await phoneProvider.verifyPhoneNumber(
        formattedPhone,
        recaptchaVerifier
      )

      set({ confirmationResult: { verificationId, phoneNumber: formattedPhone } })
      toast.success('Código OTP enviado por SMS')
      return { success: true }
    } catch (error) {
      console.error('Send OTP error:', error)
      
      let errorMessage = 'Error al enviar código OTP'
      if (error.code === 'auth/invalid-phone-number') {
        errorMessage = 'Número de teléfono inválido'
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Demasiados intentos. Intenta más tarde'
      } else if (error.code === 'auth/quota-exceeded') {
        errorMessage = 'Cuota de SMS excedida. Contacta soporte'
      }
      
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  },

  // Verify OTP code and link to account
  verifyPhoneOTP: async (code) => {
    try {
      const { confirmationResult, user } = get()
      
      if (!confirmationResult || !confirmationResult.verificationId) {
        throw new Error('No hay código pendiente de verificación')
      }

      if (!user) {
        throw new Error('Usuario no autenticado')
      }

      // Crear credencial de teléfono
      const phoneCredential = PhoneAuthProvider.credential(
        confirmationResult.verificationId,
        code
      )

      // Actualizar el número de teléfono del usuario actual (sin cambiar sesión)
      await updatePhoneNumber(user, phoneCredential)

      // Actualizar Firestore
      await updateDoc(doc(db, 'users', user.uid), {
        phoneVerified: true,
        phone: confirmationResult.phoneNumber,
        updatedAt: serverTimestamp(),
      })

      // Refresh user data
      const userDoc = await getDoc(doc(db, 'users', user.uid))
      set({ userData: { id: userDoc.id, ...userDoc.data() }, confirmationResult: null })

      toast.success('Teléfono verificado correctamente')
      return { success: true }
    } catch (error) {
      console.error('Verify OTP error:', error)
      
      let errorMessage = 'Código OTP incorrecto'
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage = 'Código inválido'
      } else if (error.code === 'auth/code-expired') {
        errorMessage = 'Código expirado. Solicita uno nuevo'
      } else if (error.code === 'auth/credential-already-in-use') {
        // Si el número ya está en uso, solo marcamos como verificado en Firestore
        await updateDoc(doc(db, 'users', user.uid), {
          phoneVerified: true,
          updatedAt: serverTimestamp(),
        })
        const userDoc = await getDoc(doc(db, 'users', user.uid))
        set({ userData: { id: userDoc.id, ...userDoc.data() }, confirmationResult: null })
        toast.success('Teléfono verificado correctamente')
        return { success: true }
      } else if (error.code === 'permission-denied' || error.message?.includes('permissions')) {
        errorMessage = 'Error de permisos. Intenta cerrar sesión y volver a entrar'
      }
      
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  },

  // Resend email verification
  resendEmailVerification: async () => {
    const { user } = get()
    if (!user) return { success: false, error: 'No user logged in' }

    try {
      await sendEmailVerification(user, {
        url: window.location.origin + '/dashboard',
        handleCodeInApp: false,
      })
      toast.success('Correo de verificación enviado')
      return { success: true }
    } catch (error) {
      console.error('Error sending verification email:', error)
      toast.error('Error al enviar correo de verificación')
      return { success: false, error: error.message }
    }
  },

  // Check and update email verification status
  checkEmailVerification: async () => {
    const { user } = get()
    if (!user) return false

    try {
      await reload(user)
      
      if (user.emailVerified) {
        // Update in Firestore
        await updateDoc(doc(db, 'users', user.uid), {
          emailVerified: true,
          updatedAt: serverTimestamp(),
        })

        // Refresh user data
        const userDoc = await getDoc(doc(db, 'users', user.uid))
        set({ userData: { id: userDoc.id, ...userDoc.data() } })

        return true
      }
      
      return false
    } catch (error) {
      console.error('Error checking email verification:', error)
      return false
    }
  },

  // Check if user has specific role
  hasRole: (role) => {
    const { userData } = get()
    if (!userData) return false
    
    if (role === 'operator') {
      return userData.role === 'operator' || userData.role === 'admin'
    }
    
    return userData.role === role
  },
}))

// Initialize auth listener
useAuthStore.getState().initialize();
